import type { RootState } from "../../../../store";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import Draggable from "react-draggable";
import type { OptionTimeframe, TradeDirection } from "../../GTT/gttTypes";
import OrderOverviewHeader from "../../common/OrderOverviewHeader";
import { getInstrumentName } from "../../GTT/gtt.helper";
import useStockDetails from "../../../../hooks/api/get/getScripDetails";
import { ReactComponent as CloserIcon } from "../../../../assets/images/close-square2.svg";
import {
  GttBodyTabsSelectors,
  reverseOptionTimeFrame,
} from "../../GTT/GTTBody/gtt.body.selector.helper";
import OrdersTabs from "../common/Tabs";
import OrdersBody, { OrderFormData } from "../common/Body";

import type {
  BalanceDisplayProps,
  Order,
  OrderTypes,
} from "../../../../models/orders";
import type { ChargesSummaryVisible } from "../../../../slices/ordersSlice";

export type OrdersProps = {
  CloseGttModal: () => void;
  exchnage: string;
  ltp: string;
  ch: number;
  chp: string;
  segMentId: number | string;
  token: string;
  onClick?: () => void;
  symbol: string;
  isEditable: boolean;
  thisState: any; // In Future, We Can Refine This
  thisOrderType?: OrderTypes;
  thisOrderTypeRLOrMKT?: OrderFormData["orderType"]; // "Limit", "Market"
  thisLotSize?: number;
  thisTriggedPrice?: number;
  thisDirection?: OrderFormData["tradeDirection"]; // "Buy", "Sell"
  thisValidity?: OrderFormData["validity"]; // "DAY", "IOC", "AMO" , ''
  thisStoplossTriggeredPrice?: number;
  thisGTTData?: Order["gtt"];
  thisStopLoss: boolean;
  thisId?: string;
  thisDisclosedQuantity?: number;
};

const Orders: React.FC<OrdersProps> = ({
  CloseGttModal,
  exchnage,
  ltp,
  ch,
  chp,
  segMentId,
  token,
  onClick,
  symbol,
  isEditable,
  thisOrderType,
  thisOrderTypeRLOrMKT,
  thisTriggedPrice,
  thisDirection,
  thisValidity,
  thisLotSize,
  thisStoplossTriggeredPrice,
  thisGTTData,
  thisStopLoss,
  thisId,
  thisDisclosedQuantity,
}) => {
  const [tradeDirection, setTradeDirection] = useState<TradeDirection>(
    thisDirection!
  );
  const [currentGttTab, setCurrentGttTab] = useState<OptionTimeframe>();
  const memorizedLtp = useMemo(() => +ltp, []);
  const [price, setPrice] = useState<number>(memorizedLtp); // -1 means market else mean value

  const { equityPreference, derivatePreference } = useSelector(
    (state: RootState) => state.orderPreference
  );

  const instrumentName = getInstrumentName(exchnage);
  const { stockDetails } = useStockDetails(+token, instrumentName);

  const ordersTabsArray = GttBodyTabsSelectors({
    marketSegmentId: stockDetails?.marketSegmentID ?? "",
    choserString: stockDetails?.instrumentName ?? "",
  });

  useEffect(() => {
    const finalSelectedTab =
      ordersTabsArray.length <= 2 ? derivatePreference : equityPreference;
    if (ordersTabsArray.length > 0 && !currentGttTab) {
      setCurrentGttTab(finalSelectedTab);
    }
    // if in case we had edit order
    if (thisOrderType && isEditable) {
      const currentTab = reverseOptionTimeFrame({
        timeframe: ordersTabsArray,
        optionFrame: thisOrderType,
      });
      setCurrentGttTab(currentTab);
    } // TODO: We may pass id here in case someone click on some other edit tab then it wpuld be helpfull
  }, [ordersTabsArray, currentGttTab, ordersTabsArray.length]);

  const toggleTradeDirection = () => {
    setTradeDirection((prev) => (prev === "Buy" ? "Sell" : "Buy"));
  };

  const currentGttSwitcher = (newGttTab: OptionTimeframe) => {
    setCurrentGttTab(newGttTab);
  };

  if (!currentGttTab || !token || !instrumentName || !ltp) {
    return null;
  }

  if (!tradeDirection || !stockDetails?.lotSize || !stockDetails?.tick) {
    return null;
  }

  const orderState: ChargesSummaryVisible = {
    memorizedLTP: memorizedLtp,
    buySell: tradeDirection === "Buy" ? "BUY" : "SELL",
    exchange: exchnage,
    instrumentType: "",
    price: memorizedLtp,
    quantity: 1,
    symbol: stockDetails?.symbol ?? "",
    tradeType: currentGttTab,
    isVisible: false,
  };

  const isIceBerg = !(ordersTabsArray.length === 4);

  return (
    <Draggable handle=".handle_b" bounds="parent" allowAnyClick>
      <div
        className="custom-order-dialog_basket currentBg draggable-border"
        style={{ overflow: "hidden" }}
      >
        <div
          className="container optionStartegyHead handle_b"
          style={{ height: 80 }}
        >
          <OrderOverviewHeader
            toggleTradeDirection={toggleTradeDirection}
            tradeDirection={tradeDirection}
            exchnage={instrumentName.toUpperCase()}
            scriptName={stockDetails?.symbol || ""}
            securityDesc={stockDetails?.securityDesc || ""}
            ltp={ltp}
            ch={ch}
            chp={chp}
            disabled={isEditable}
          />

          <div className="flex c-pointer no-drag" onClick={CloseGttModal}>
            <CloserIcon />
          </div>
        </div>

        {ordersTabsArray.length > 0 && (
          <OrdersTabs
            optionTimeframe={ordersTabsArray}
            selected={currentGttTab}
            toggleOptionTimeframe={currentGttSwitcher}
            token={+token}
            exchange={exchnage}
            tradeDirection={tradeDirection}
            variety={"MKT"} // as of now consider it constant
            ltp={+ltp}
            isEditable={isEditable}
            quantity={stockDetails.lotSize}
            segMentId={+segMentId}
            price={price}
          />
        )}

        {/* GTT Body */}
        <OrdersBody
          CloseGttModal={CloseGttModal}
          memorizedLtp={memorizedLtp}
          lotSize={stockDetails.lotSize}
          tick={stockDetails.tick}
          orderState={orderState}
          symbol={symbol}
          onClick={onClick}
          tradeDirection={tradeDirection}
          token={+token}
          exchange={exchnage}
          segMentId={+segMentId}
          isIceBerg={isIceBerg}
          thisOrderTypeRLOrMKT={thisOrderTypeRLOrMKT}
          thisTriggedPrice={thisTriggedPrice}
          thisValidity={thisValidity}
          thisLotSize={thisLotSize}
          instrumentName={stockDetails?.instrumentName}
          // code
          selectedTab={currentGttTab}
          thisStoplossTriggeredPrice={thisStoplossTriggeredPrice}
          thisGTTData={thisGTTData}
          isEditable={isEditable}
          thisStopLoss={thisStopLoss}
          thisId={thisId}
          thisDisclosedQuantity={thisDisclosedQuantity}
          setPrice={setPrice}
        />
      </div>
    </Draggable>
  );
};

export default Orders;
